import { ComponentProps, FC, forwardRef, ReactNode, useMemo } from 'react';
import Box from '@components/Layout/Grid/Box';

import IconContainer, { StyledInput, InputProps as StyledInputProps } from './styles';
import Icon from '../../Images/Icon';
import maskString from './utils';

type InputAdornment =
    | { icon?: string | ComponentProps<typeof Icon>; trailing?: never }
    | { icon?: never; trailing?: ReactNode };

export type InputProps = StyledInputProps &
    InputAdornment & {
        hasKeyMask?: boolean;
    };

export const KeyInput: FC<React.PropsWithChildren<InputProps>> = forwardRef((props, ref) => {
    const { value, onChange } = props;

    const maskedValue = value ? maskString(value) : value;

    return (
        <>
            <input
                style={{ position: 'absolute', visibility: 'hidden' }}
                value={value}
                ref={ref}
                readOnly
            />
            <StyledInput {...props} pr={0} value={maskedValue} onChange={onChange} />
        </>
    );
});

const Input: FC<React.PropsWithChildren<InputProps>> = forwardRef(
    ({ icon, trailing, hasKeyMask, ...props }, ref) => {
        const Component = !hasKeyMask ? StyledInput : KeyInput;

        const renderedIcon = useMemo(() => {
            if (!icon) return null;

            return typeof icon === 'string' ? (
                <Icon icon={icon} fill="base.40" size="small" />
            ) : (
                <Icon {...icon} />
            );
        }, [icon]);

        const adornment = trailing ?? renderedIcon;

        if (!adornment) return <Component {...props} ref={ref} />;

        return (
            <Box position="relative" width={1}>
                <Component ref={ref} {...props} />
                <IconContainer position="absolute" top="50%" right="0">
                    {adornment}
                </IconContainer>
            </Box>
        );
    },
);

export default Input;
